// utils/nativePermissions.js
//
// Option A bridge: the web app NEVER shows permission UI. At each permission
// moment it sends one message to the native shell (with the soft-ask + denial
// copy already token-filled). The NATIVE shell renders the soft-ask, fires the
// real OS prompt, and on decline shows the denial/recovery card itself. It then
// replies with the final status so the web can decide whether to continue.
//
// In a plain browser (no native shell) this is a no-op that returns
// "unavailable" — callers must keep their existing browser behaviour for that
// case (web app stays exactly as it is today).
//
// Protocol (matches the app's existing postMessage convention —
// see initNotificationSocket.js, ContactSyncPopup.jsx):
//
//   Web → Native:
//     ReactNativeWebView.postMessage(JSON.stringify({
//       type: "REQUEST_PERMISSION",
//       permission: "MICROPHONE",
//       requestId: "MICROPHONE_1",
//       softAsk: { title, body, allowLabel, denyLabel },  // native renders this
//       denied: "…recovery text…",                        // native shows on decline
//     }))
//
//   Native → Web (delivered to the WebView as a JSON string):
//     { type: "PERMISSION_RESULT", requestId: "MICROPHONE_1",
//       status: "granted" | "denied" | "blocked" | "unavailable" }

import { getPermissionCopy } from "../lib/permissionCopy";

export const PermissionStatus = {
  GRANTED: "granted",
  DENIED: "denied", // declined, can re-ask later
  BLOCKED: "blocked", // OS won't prompt again — Settings only
  UNAVAILABLE: "unavailable", // not inside the native shell (plain browser)
};

export const isNativeApp = () => !!window.ReactNativeWebView;

let _seq = 0;
const _pending = new Map(); // requestId -> { resolve, timer }

let _listenerAttached = false;
const _attachListener = () => {
  if (_listenerAttached) return;
  _listenerAttached = true;

  const handle = (event) => {
    let msg;
    try {
      msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return; // not JSON — not ours
    }
    if (msg?.type !== "PERMISSION_RESULT" || !msg.requestId) return;

    const entry = _pending.get(msg.requestId);
    if (!entry) return;
    clearTimeout(entry.timer);
    _pending.delete(msg.requestId);
    entry.resolve(msg.status || PermissionStatus.DENIED);
  };

  // iOS delivers on window, Android on document — listen on both.
  window.addEventListener("message", handle);
  document.addEventListener("message", handle);
};

/**
 * Ask the native shell to run the full permission flow (soft-ask → OS prompt →
 * denial recovery) for one permission, then resolve with the final status.
 *
 * @param {"MICROPHONE"|"CONTACTS"|"NOTIFICATIONS"|"LOCATION"|"CAMERA"|"PHOTOS"} permission
 * @param {{agent?: string, app?: string}} tokens  fills {Agent} / {App} in the copy
 * @returns {Promise<"granted"|"denied"|"blocked"|"unavailable">}
 */
export const requestNativePermission = (
  permission,
  tokens = {},
  { timeoutMs = 120000, skipSoftAsk = false } = {}
) => {
  // Plain browser → no native shell. Caller keeps its existing behaviour.
  if (!isNativeApp()) return Promise.resolve(PermissionStatus.UNAVAILABLE);

  _attachListener();
  const copy = getPermissionCopy(permission, tokens);
  const requestId = `${permission}_${++_seq}`;

  return new Promise((resolve) => {
    // If native never answers (old shell / crash) don't hang forever.
    const timer = setTimeout(() => {
      _pending.delete(requestId);
      resolve(PermissionStatus.DENIED);
    }, timeoutMs);

    _pending.set(requestId, { resolve, timer });

    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "REQUEST_PERMISSION",
          permission,
          requestId,
          // When skipSoftAsk is true the caller has already shown its own
          // ask (e.g. PushPermissionModal) — native goes straight to the OS prompt.
          skipSoftAsk,
          softAsk:
            skipSoftAsk || !copy
              ? null
              : {
                  title: copy.title,
                  body: copy.body,
                  allowLabel: copy.allowLabel,
                  denyLabel: copy.denyLabel,
                },
          denied: copy?.denied || null,
        })
      );
    } catch {
      clearTimeout(timer);
      _pending.delete(requestId);
      resolve(PermissionStatus.DENIED);
    }
  });
};

// Notifications are asked ONCE from the product surface that owns the prompt.
// This flag dedups across reloads, so the prompt fires exactly once.
const NOTIF_ASKED_KEY = "rexpt_notif_perm_asked";

/**
 * Ask for NOTIFICATIONS permission exactly once, ever — native shell only.
 * Native renders the popup, runs the OS prompt and emits the FCM token.
 * Safe to call from multiple places; only the first call does anything.
 */
export const askNotificationPermissionOnce = (tokens = {}) => {
  if (!isNativeApp()) return;
  try {
    if (localStorage.getItem(NOTIF_ASKED_KEY)) return;
    localStorage.setItem(NOTIF_ASKED_KEY, "1");
  } catch {
    /* localStorage unavailable — fall through and still ask once this session */
  }
  requestNativePermission("NOTIFICATIONS", { app: "Rexpt", ...tokens });
};
